/**
 * API Client Configuration
 *
 * Axios instance with authentication, interceptors, and error handling
 * for DMS FastAPI backend integration.
 *
 * SECURITY: Uses HttpOnly cookies for JWT token storage.
 * - All requests include credentials: 'include' to send/receive cookies
 * - Tokens are NOT stored in localStorage (XSS protection)
 * - Cookies are managed by the browser and server
 */

import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { logger } from '@shared/utils/logger';

/**
 * API Error structure returned from FastAPI
 */
export interface ApiError {
  detail: string | Record<string, unknown>;
  status_code?: number;
}

/**
 * Generic API response wrapper
 */
export interface ApiResponse<T> {
  data: T;
  status: number;
  message?: string;
}

/**
 * Retry configuration
 */
const MAX_RETRIES = 3;
const BASE_DELAY_MS = 1000; // Base delay: 1 second (2 seconds for 429)
const RETRYABLE_STATUS_CODES = [408, 429, 500, 502, 503, 504]; // 429 now retryable with exponential backoff

/**
 * Redirect loop protection
 * Prevents rapid-fire redirects on 401 errors (especially during ngrok testing)
 */
let lastRedirectTime = 0;
const REDIRECT_COOLDOWN_MS = 5000; // 5 seconds between redirects

/**
 * Circuit Breaker for Token Refresh
 * Prevents infinite refresh loops when refresh endpoint fails repeatedly.
 *
 * CRITICAL FIX: This implementation:
 * 1. Tracks failures at MODULE level (shared across all requests)
 * 2. Forces immediate logout when circuit opens (clears localStorage AND redirects)
 * 3. Prevents refresh endpoint 401s from triggering another refresh attempt
 * 4. Uses promise subscription pattern to prevent concurrent refresh attempts
 * 5. Auto-resets circuit breaker after timeout to allow recovery
 */
let refreshFailureCount = 0;
let isRefreshInProgress = false;  // Mutex to prevent concurrent refreshes
let refreshPromise: Promise<void> | null = null;  // Promise for in-flight refresh (subscription pattern)
let forceLogoutTriggered = false; // Prevents multiple forced logouts
let circuitOpenedAt: number | null = null;  // Timestamp when circuit opened (for auto-reset)
const MAX_REFRESH_FAILURES = 3;
const CIRCUIT_RESET_MS = 30000;  // 30 seconds - auto-reset circuit breaker

/**
 * Force logout - clears auth state and redirects to login
 * Called when circuit breaker opens (3 consecutive refresh failures)
 */
const forceLogout = (): void => {
  if (forceLogoutTriggered) {
    console.warn('[API] Force logout already triggered, skipping duplicate');
    return;
  }
  forceLogoutTriggered = true;

  console.error(`[API] CIRCUIT BREAKER OPEN - ${refreshFailureCount} consecutive refresh failures`);
  console.error('[API] FORCING LOGOUT - clearing all auth state');

  // Clear localStorage auth state IMMEDIATELY
  if (typeof window !== 'undefined') {
    localStorage.removeItem('dms-auth-storage');

    // Dispatch session expired event for any listeners
    window.dispatchEvent(new CustomEvent('auth:session-expired', {
      detail: { reason: 'circuit-breaker-force-logout', failures: refreshFailureCount }
    }));

    // IMMEDIATE redirect to login page - don't wait for next request
    // Use setTimeout(0) to allow current stack to complete
    setTimeout(() => {
      console.error('[API] Redirecting to /login');
      window.location.href = '/login';
    }, 0);
  }
};

/**
 * Check if circuit is open (too many failures)
 * Returns true if we should NOT attempt refresh
 *
 * CRITICAL: Auto-resets after CIRCUIT_RESET_MS to allow recovery from
 * transient failures (e.g., temporary network issues, server restarts)
 */
const isCircuitOpen = (): boolean => {
  if (refreshFailureCount < MAX_REFRESH_FAILURES) {
    return false;
  }

  // Circuit is "open" - check if we should auto-reset
  if (circuitOpenedAt && (Date.now() - circuitOpenedAt > CIRCUIT_RESET_MS)) {
    console.log('[API] Circuit breaker auto-reset after timeout');
    circuitOpenedAt = null;
    refreshFailureCount = 0;
    forceLogoutTriggered = false;
    return false;
  }

  return true;
};

/**
 * Record a refresh failure and check if we should force logout
 */
const recordRefreshFailure = (): void => {
  refreshFailureCount++;
  console.error(`[API] Refresh failure ${refreshFailureCount}/${MAX_REFRESH_FAILURES}`);

  if (refreshFailureCount >= MAX_REFRESH_FAILURES) {
    // Record when circuit opened for auto-reset timeout
    circuitOpenedAt = Date.now();
    forceLogout();
  }
};

/**
 * Record a successful refresh - resets failure count
 */
const recordRefreshSuccess = (): void => {
  if (refreshFailureCount > 0) {
    console.log('[API] Refresh succeeded, resetting failure count');
  }
  refreshFailureCount = 0;
  circuitOpenedAt = null;  // Reset circuit timestamp
  forceLogoutTriggered = false; // Reset so future sessions can logout if needed
};

/**
 * Get API base URL from environment or use Vite proxy
 *
 * Priority:
 * 1. localStorage override (for mobile testing without rebuild)
 * 2. VITE_API_BASE_URL environment variable
 * 3. Default: Empty string (uses Vite proxy which routes to Railway backend)
 *
 * NOTE: The Vite dev server proxy in vite.config.ts routes /api/* to Railway production backend.
 * This works for both localhost AND ngrok tunnels (ngrok → Vite → proxy → Railway).
 *
 * To use local backend instead, either:
 * - Set localStorage: localStorage.setItem('api_base_url_override', 'http://127.0.0.1:8001')
 * - Change vite.config.ts proxy target to 'http://127.0.0.1:8001'
 */
const getBaseURL = (): string => {
  // Check localStorage override first (for mobile testing convenience)
  if (typeof window !== 'undefined') {
    const override = localStorage.getItem('api_base_url_override');
    if (override) {
      logger.log(`[API] Using localStorage override: ${override}`);
      return override;
    }
  }

  // PRODUCTION FIX (2025-12-26): Use relative paths in production
  // This makes API requests go through Cloudflare Pages Functions proxy at /api/*
  // which forwards to Railway. This fixes HttpOnly cookie issues because:
  // - Cookies are now first-party (both frontend and API appear from same domain)
  // - No third-party cookie blocking from browsers
  // - CORS is handled by the proxy, not cross-origin
  //
  // The proxy is defined in: functions/api/[[path]].ts
  // Supported domains: eroland.me, cenari.net
  if (typeof window !== 'undefined' && (window.location.host === 'eroland.me' || window.location.host === 'cenari.net')) {
    logger.log('[API] Using Cloudflare proxy (relative path for first-party cookies)');
    return ''; // Relative path - goes through Cloudflare Functions proxy
  }

  // Check environment variable (used for local development with Vite proxy)
  // IMPORTANT: Don't use this in production (handled above) because it bypasses the proxy
  if (import.meta.env.DEV && import.meta.env.VITE_API_BASE_URL) {
    logger.log(`[API] DEV: Using environment variable: ${import.meta.env.VITE_API_BASE_URL}`);
    return import.meta.env.VITE_API_BASE_URL;
  }

  // Default: Use relative path which goes through Vite proxy to Railway backend
  // This works for both localhost development AND ngrok tunnels
  logger.log('[API] Using Vite proxy (relative path) → Railway backend');
  return '';
};

/**
 * Check if user is authenticated.
 *
 * SECURITY: JWT tokens are now stored in HttpOnly cookies (not localStorage).
 * This function checks the auth state from Zustand store, which persists
 * only the user info and isAuthenticated flag (not tokens).
 *
 * The actual token validation happens on the server when cookies are sent
 * with each request (via withCredentials: true).
 *
 * @returns {boolean} True if user appears to be authenticated
 */
const isAuthenticated = (): boolean => {
  // Check Zustand persist storage for auth state (not tokens)
  const authData = localStorage.getItem('dms-auth-storage');

  if (authData) {
    try {
      const parsed = JSON.parse(authData);
      return parsed.state?.isAuthenticated === true;
    } catch (error) {
      console.error('[API Client] Failed to parse auth storage:', error);
      return false;
    }
  }

  return false;
};

/**
 * Sleep utility for retry delays
 */
const sleep = (ms: number): Promise<void> => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

/**
 * Determine if an error is retryable
 */
const isRetryableError = (error: AxiosError): boolean => {
  if (!error.response) {
    // Network errors (no response) are retryable
    return true;
  }

  const status = error.response.status;
  return RETRYABLE_STATUS_CODES.includes(status);
};

/**
 * Create configured Axios instance
 *
 * SECURITY: Uses withCredentials: true to send/receive HttpOnly cookies.
 * This is required for cookie-based authentication.
 */
const createApiClient = (): AxiosInstance => {
  const client = axios.create({
    baseURL: `${getBaseURL()}/api`,
    timeout: 30000, // 30 seconds
    headers: {
      'Content-Type': 'application/json',
      // Add ngrok-skip-browser-warning header for mobile testing
      'ngrok-skip-browser-warning': 'true',
    },
    // SECURITY: Required for HttpOnly cookie authentication
    // This tells the browser to include cookies with cross-origin requests
    withCredentials: true,
  });

  /**
   * Request Interceptor
   * - Log requests in development mode
   *
   * SECURITY: JWT tokens are in HttpOnly cookies and sent automatically.
   * No need to manually add Authorization header.
   */
  client.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      // SECURITY: Tokens are in HttpOnly cookies, sent automatically via withCredentials
      // Log auth status for debugging (cannot access actual token)
      if (import.meta.env.DEV) {
        console.log('[API Client] Request with credentials:', config.withCredentials);
        console.log('[API Client] Auth state:', isAuthenticated() ? 'authenticated' : 'not authenticated');
      }

      // Log requests in development
      if (import.meta.env.DEV) {
        logger.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`, {
          params: config.params,
          data: config.data,
        });
      }

      return config;
    },
    (error: AxiosError) => {
      logger.error('[API Request Error]', error);
      return Promise.reject(error);
    }
  );

  /**
   * Response Interceptor
   * - Handle errors globally
   * - Automatic token refresh on 401
   * - Transform error responses
   * - Log responses in development
   */
  client.interceptors.response.use(
    (response: AxiosResponse) => {
      // Log successful responses in development
      if (import.meta.env.DEV) {
        logger.log(`[API Response] ${response.config.method?.toUpperCase()} ${response.config.url}`, {
          status: response.status,
          data: response.data,
        });
      }

      return response;
    },
    async (error: AxiosError<ApiError>) => {
      // Log full error details for debugging
      console.error('[API Client] Request failed:', {
        url: error.config?.url,
        method: error.config?.method,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        headers: error.config?.headers,
        message: error.message,
      });
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: number; _tokenRefreshAttempt?: boolean };

      // Log errors
      logger.error('[API Error]', {
        url: error.config?.url,
        status: error.response?.status,
        detail: error.response?.data?.detail,
      });

      // Handle specific error codes
      if (error.response) {
        const status = error.response.status;
        const detail = error.response.data?.detail;

        switch (status) {
          case 401:
            // Unauthorized - attempt token refresh with circuit breaker
            // SECURITY: Token refresh uses HttpOnly cookies, no manual token handling needed

            // CRITICAL FIX #1: If this IS the refresh endpoint, DON'T try to refresh again!
            // This is the key fix for the infinite loop
            const requestUrl = originalRequest?.url || '';
            if (requestUrl.includes('/auth/refresh')) {
              console.error('[API] /auth/refresh returned 401 - refresh token is invalid');
              // Record this as a refresh failure
              recordRefreshFailure();
              return Promise.reject(error);
            }

            // CRITICAL FIX #2: Check circuit breaker FIRST (before localStorage check)
            // If circuit is already open, force logout was already triggered
            if (isCircuitOpen()) {
              console.error('[API] Circuit breaker already open - rejecting request');
              // forceLogout() was already called, just reject
              return Promise.reject(new Error('Session expired - please log in again'));
            }

            // Only attempt refresh once per request
            if (!originalRequest._tokenRefreshAttempt) {
              originalRequest._tokenRefreshAttempt = true;

              // Check if user was previously authenticated (localStorage check)
              // This prevents pre-auth API calls from triggering refresh
              if (!isAuthenticated()) {
                logger.log('[API] 401 received but user not authenticated - skipping token refresh');
                return Promise.reject(error);
              }

              // CRITICAL FIX #3: Promise subscription pattern for concurrent refresh attempts
              // Instead of blind waiting, subscribe to the in-flight refresh result
              // This prevents the deadlock where waiting requests loop infinitely
              if (isRefreshInProgress && refreshPromise) {
                console.log('[API] Refresh already in progress, subscribing to result...');
                try {
                  // Subscribe to the in-flight refresh - wait for its result
                  await refreshPromise;
                  // Refresh succeeded - retry with new token
                  console.log('[API] Subscribed refresh succeeded, retrying request');
                  return client(originalRequest);
                } catch (subscribeError) {
                  // Refresh failed - reject immediately, don't loop
                  console.error('[API] Subscribed refresh failed, rejecting request');
                  return Promise.reject(new Error('Session expired - please log in again'));
                }
              }

              isRefreshInProgress = true;

              // Create the refresh promise that other requests can subscribe to
              refreshPromise = (async () => {
                // Dynamically import authStore to avoid circular dependency
                const { useAuthStore } = await import('@shared/store/authStore');

                // Attempt token refresh
                // SECURITY: This calls POST /auth/refresh which reads refresh_token from HttpOnly cookie
                console.log('[API] Attempting token refresh...');
                await useAuthStore.getState().refreshAccessToken();
              })();

              try {
                await refreshPromise;

                // Success - record it and retry request
                console.log('[API] Token refresh successful');
                recordRefreshSuccess();

                // Retry original request - new access_token cookie will be sent automatically
                return client(originalRequest);
              } catch (refreshError) {
                // Refresh failed - recordRefreshFailure handles forceLogout if needed
                console.error('[API] Token refresh failed');
                recordRefreshFailure();

                // If circuit just opened, forceLogout() was called inside recordRefreshFailure()
                return Promise.reject(new Error('Session expired - please log in again'));
              } finally {
                // Always clean up mutex state
                isRefreshInProgress = false;
                refreshPromise = null;
              }
            }
            break;

          case 403:
            // Forbidden - insufficient permissions
            logger.error('Access denied. Insufficient permissions.');
            break;

          case 404:
            // Not Found
            logger.error(`Resource not found: ${error.config?.url}`);
            break;

          case 422:
            // Validation Error (FastAPI)
            logger.error('Validation error:', detail);
            break;

          case 429:
            // Rate Limit - now retryable with exponential backoff
            logger.warn('Rate limit exceeded (429). Will retry with exponential backoff.');
            // Check if we have a Retry-After header
            if (error.response?.headers?.['retry-after']) {
              const retryAfter = error.response.headers['retry-after'];
              const retrySeconds = parseInt(retryAfter, 10) || retryAfter;
              logger.warn(`Server requests retry after ${retrySeconds} seconds`);
            }
            break;

          case 500:
          case 502:
          case 503:
          case 504:
            // Server errors - attempt retry
            break;
        }
      }

      // Retry logic for network errors and server errors
      if (isRetryableError(error) && originalRequest) {
        const retryCount = originalRequest._retry || 0;

        if (retryCount < MAX_RETRIES) {
          originalRequest._retry = retryCount + 1;

          // Exponential backoff with special handling for 429
          const status = error.response?.status;
          const baseDelay = status === 429 ? 2000 : BASE_DELAY_MS;
          const delay = baseDelay * Math.pow(2, retryCount);

          logger.log(`[API Retry] ${status || 'Network'} error - attempt ${retryCount + 1}/${MAX_RETRIES} after ${delay}ms`);

          await sleep(delay);

          return client(originalRequest);
        }
      }

      return Promise.reject(error);
    }
  );

  return client;
};

/**
 * Global API client instance
 */
export const apiClient = createApiClient();

/**
 * Transform technical database errors into user-friendly messages
 *
 * @param detail - Error detail from backend
 * @returns User-friendly error message
 */
const transformDatabaseError = (detail: string): string => {
  // PostgreSQL constraint violations
  if (detail.includes('psycopg2.errors.CheckViolation') || detail.includes('CheckViolation')) {
    if (detail.includes('campaigns_status_check')) {
      return 'Invalid status value. Please use a valid campaign status (planning, future, active, ongoing, completed, or archived).';
    }
    if (detail.includes('campaigns_priority_check')) {
      return 'Invalid priority value. Please use a valid priority (low, medium, high, or urgent).';
    }
    return 'The requested change violates database rules. Please check your input values.';
  }

  // PostgreSQL unique constraint violations
  if (detail.includes('psycopg2.errors.UniqueViolation') || detail.includes('UniqueViolation')) {
    if (detail.includes('campaigns') && detail.includes('active')) {
      return 'Only one campaign can be active at a time. Please deactivate the current active campaign first.';
    }
    return 'This value already exists. Please use a different value.';
  }

  // Invalid status transitions
  if (detail.includes('Invalid status transition')) {
    const match = detail.match(/'([^']+)'\s*→\s*'([^']+)'/);
    if (match) {
      const [, currentStatus, newStatus] = match;
      return `Cannot change status from "${currentStatus}" to "${newStatus}". Please follow the campaign workflow steps.`;
    }
    return 'Invalid status transition. Please follow the campaign workflow.';
  }

  // Generic PostgreSQL errors
  if (detail.includes('psycopg2')) {
    return 'Database error. The change was not saved. Please try again or contact support.';
  }

  // Return original detail if no transformation applies
  return detail;
};

/**
 * Type-safe API error handler with user-friendly transformations
 *
 * @param error - Axios error
 * @returns Formatted user-friendly error message
 */
export const handleApiError = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<ApiError>;

    // Return FastAPI detail message if available (with transformation)
    if (axiosError.response?.data?.detail) {
      const detail = axiosError.response.data.detail;

      // Handle both string and object detail formats
      if (typeof detail === 'string') {
        return transformDatabaseError(detail);
      }

      // FastAPI validation errors are objects
      return 'Validation error. Please check your input.';
    }

    // Network error (no response)
    if (!axiosError.response) {
      return 'Network error. Please check your connection.';
    }

    // HTTP status-specific messages
    const status = axiosError.response.status;
    switch (status) {
      case 400:
        return 'Bad request. Please check your input and try again.';
      case 403:
        return 'Access denied. You do not have permission to perform this action.';
      case 404:
        return 'Resource not found. It may have been deleted.';
      case 422:
        return 'Validation error. Please check your input values.';
      case 500:
        return 'Server error. Please try again later or contact support.';
      case 502:
      case 503:
      case 504:
        return 'Server temporarily unavailable. Please try again in a moment.';
      default:
        return `Request failed (error ${status}). Please try again.`;
    }
  }

  // Unknown error type
  logger.error('Unknown error type in handleApiError:', error);
  return 'An unexpected error occurred.';
};

/**
 * Check if API is reachable
 *
 * @returns Promise resolving to true if API is healthy
 */
export const checkApiHealth = async (): Promise<boolean> => {
  try {
    const response = await apiClient.get('/health');
    return response.status === 200;
  } catch (error) {
    logger.error('API health check failed:', error);
    return false;
  }
};

/**
 * Set API base URL override (for mobile testing)
 *
 * @param url - Base URL (e.g., "https://abc123.ngrok-free.app")
 */
export const setApiBaseUrlOverride = (url: string | null): void => {
  if (url) {
    localStorage.setItem('api_base_url_override', url);
    logger.log(`[API] Base URL override set to: ${url}`);
    logger.log('[API] Reload the page to apply changes');
  } else {
    localStorage.removeItem('api_base_url_override');
    logger.log('[API] Base URL override removed');
    logger.log('[API] Reload the page to apply changes');
  }
};

/**
 * Get current API base URL (for debugging)
 */
export const getApiBaseUrl = (): string => {
  return getBaseURL();
};

// Expose helper functions to window for console access (development only)
if (import.meta.env.DEV && typeof window !== 'undefined') {
  (window as Window & {
    apiClient?: {
      setBaseUrl: (url: string | null) => void;
      getBaseUrl: () => string;
      checkHealth: () => Promise<boolean>;
    };
  }).apiClient = {
    setBaseUrl: setApiBaseUrlOverride,
    getBaseUrl: getApiBaseUrl,
    checkHealth: checkApiHealth,
  };
  logger.log('[API] Helper functions exposed to console:');
  logger.log('  window.apiClient.setBaseUrl(url)  - Set API URL override');
  logger.log('  window.apiClient.getBaseUrl()     - Get current API URL');
  logger.log('  window.apiClient.checkHealth()    - Check API health');
}

export default apiClient;
